/**
 * Pure functions that turn the loaded board, the staged selection and the
 * clock into the per game state labels, the match card props and the
 * status bar props, so the wiring hook only calls them (S2, S3, S5, S6).
 *
 * No React, no clock reads (`nowMs` and `timeZone` come in), no navigation.
 */
import type {
  typeBoardData,
  typeEntryStatus,
  typePickTile,
} from "../pick/pickBoardModel";
import { formatKickoffLabel } from "../pick/pickDeadlineModel";
import type { typePickSelection } from "../pick/pickSubmitModel";
import type { typeTeamName } from "../pick/useTeamNames";
import {
  formatGameStateLabel,
  resolvePickTone,
  type typePickTone,
} from "./survivorCardModel";
import type {
  typeSurvivorPrimaryAction,
  typeSurvivorStatusBarProps,
} from "./SurvivorStatusBar";
import {
  formatEntryStatus,
  formatLockCountdown,
  formatSubmittedLabel,
  resolveMissedPickNote,
} from "./survivorStatusModel";
import type { typeSurvivorGameView } from "./useSurvivorGameData";

export type typeMatchCardView = {
  tile: typePickTile;
  gameStateLabel: string;
  homeLogoUrl: string | null;
  awayLogoUrl: string | null;
  tone: typePickTone;
  toneLabel: string | null;
  priority: { value: number; count: number } | null;
};

export function buildGameStateLabels(input: {
  boardData: typeBoardData | null;
  timeZone: string;
  nowMs: number;
}): Record<string, string> {
  const boardData = input.boardData;
  if (boardData === null) return {};
  const labels: Record<string, string> = {};
  for (const game of boardData.games) {
    labels[game.eventId] = formatGameStateLabel({
      status: game.status,
      kickoffAt: game.kickoffAt,
      kickoffLabel: formatKickoffLabel({
        isoInstant: game.kickoffAt,
        timeZone: input.timeZone,
      }),
      nowMs: input.nowMs,
    });
  }
  return labels;
}

export function buildMatchCardViews(input: {
  tiles: readonly typePickTile[];
  selections: readonly typePickSelection[];
  savedPicks: typeBoardData["myPicks"];
  gameStateLabelByEventId: Readonly<Record<string, string>>;
  teamsById: ReadonlyMap<string, typeTeamName>;
  showPriority: boolean;
}): typeMatchCardView[] {
  return input.tiles.map((tile) => {
    const selectionIndex = input.selections.findIndex(
      (selection) => selection.eventId === tile.eventId,
    );
    const savedPick =
      input.savedPicks.find((pick) => pick.eventId === tile.eventId) ?? null;
    const { tone, label } = resolvePickTone({
      selection:
        selectionIndex >= 0 ? (input.selections[selectionIndex] ?? null) : null,
      savedPick,
    });
    const priority =
      selectionIndex >= 0 && input.showPriority
        ? { value: selectionIndex + 1, count: input.selections.length }
        : null;
    return {
      tile,
      gameStateLabel: input.gameStateLabelByEventId[tile.eventId] ?? "",
      homeLogoUrl: input.teamsById.get(tile.home.teamId)?.logoUrl ?? null,
      awayLogoUrl: input.teamsById.get(tile.away.teamId)?.logoUrl ?? null,
      tone,
      toneLabel: label,
      priority,
    };
  });
}

export function buildSurvivorStatusBar(input: {
  viewKind: typeSurvivorGameView["kind"];
  hasEntry: boolean;
  entryStatus: typeEntryStatus | null;
  boardData: typeBoardData | null;
  nowMs: number;
  primaryAction: typeSurvivorPrimaryAction | null;
}): typeSurvivorStatusBarProps | null {
  if (
    input.viewKind === "loading" ||
    input.viewKind === "failed" ||
    input.viewKind === "noContest" ||
    input.viewKind === "waiting"
  ) {
    return null;
  }
  const boardData = input.boardData;
  const { label, tone } = formatEntryStatus(
    input.hasEntry ? input.entryStatus : null,
  );
  const savedCount = boardData?.myPicks.length ?? 0;
  const requiredCount = boardData?.requiredPickCount ?? 0;
  return {
    statusLabel: label,
    statusTone: tone,
    submittedLabel:
      boardData === null
        ? null
        : formatSubmittedLabel({ savedCount, requiredCount }),
    lockLabel:
      boardData === null
        ? null
        : formatLockCountdown({
            locksAtIso: boardData.locksAt,
            nowMs: input.nowMs,
            isLocked: boardData.locked,
          }),
    missedPickNote: resolveMissedPickNote({
      isOpen: input.viewKind === "open",
      savedCount,
      requiredCount,
    }),
    primaryAction: input.primaryAction,
  };
}
